import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface LoyaltyWallet {
  userId?: number | string;
  activePoints: number;
  equivalentCurrencyValue: number;
  currencyPerPoint: number;
  minPointsToRedeem: number;
  maxDiscountPercentage: number;
  isEnabled: boolean;
}

export interface LoyaltyRedeemResult {
  points_to_use: number;
  monetary_discount: number;
  available_balance: number;
  sufficient_balance: boolean;
}

export interface PointTransaction {
  id: number;
  wallet_id: number;
  type: 'earn' | 'spend';
  points: number;
  balance_after: number;
  action?: string;
  reference_id?: number;
  reference_type?: string;
  description?: string;
  created_at: string;
}

export interface PaginatedTransactions {
  data: PointTransaction[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface WalletTransactionsResponse {
  wallet: {
    balance: number;
    point_monetary_value: number;
    balance_in_egp: number;
  };
  transactions: PaginatedTransactions;
}

@Injectable({
  providedIn: 'root'
})
export class LoyaltyPointsService {
  private http = inject(HttpClient);
  private walletUrl = `${environment.baseUrl}wallet`;

  /**
   * Get the logged-in user's points wallet balance from backend API (/api/wallet/balance)
   */
  getMyWallet(): Observable<{ data: LoyaltyWallet; status: boolean } | null> {
    return this.http.get<any>(`${this.walletUrl}/balance`).pipe(
      map(res => {
        if (res && res.data) {
          const raw = res.data;
          const balance = Number(raw.balance ?? 0);
          const rate = Number(raw.point_monetary_value ?? 0.1);
          const balanceInEgp = Number(raw.balance_in_egp ?? +(balance * rate).toFixed(2));
          const isEnabled = raw.points_enabled !== undefined ? Boolean(raw.points_enabled) : true;

          const wallet: LoyaltyWallet = {
            activePoints: balance,
            equivalentCurrencyValue: balanceInEgp,
            currencyPerPoint: rate,
            minPointsToRedeem: raw.min_points_to_redeem ?? 10,
            maxDiscountPercentage: raw.max_discount_percentage ?? 50,
            isEnabled: isEnabled
          };
          return { data: wallet, status: true };
        }
        return null;
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Get user's points transactions history (/api/wallet/transactions)
   */
  getMyTransactions(page: number = 1, type?: string): Observable<WalletTransactionsResponse | null> {
    let url = `${this.walletUrl}/transactions?page=${page}`;
    if (type && (type === 'earn' || type === 'spend')) {
      url += `&type=${type}`;
    }
    return this.http.get<any>(url).pipe(
      map(res => (res && res.data ? res.data : null)),
      catchError(() => of(null))
    );
  }

  /**
   * Calculate discount for a given number of points (/api/wallet/calculate-discount)
   */
  calculateDiscount(pointsToUse: number): Observable<LoyaltyRedeemResult | null> {
    return this.http.post<any>(`${this.walletUrl}/calculate-discount`, {
      points_to_use: pointsToUse
    }).pipe(
      map(res => res?.data || null),
      catchError(() => of(null))
    );
  }
}
